// Set to true to force "Buy in the next Xh Ym to ship today" for testing (set back to false when done)
const FORCE_SAME_DAY_SHIPPING_FOR_TESTING = false;

function isShopifyCartPagePath() {
  var cartUrl = window.routes?.cart_url;
  if (!cartUrl) return false;
  try {
    return new URL(cartUrl, window.location.href).pathname === window.location.pathname;
  } catch (e) {
    return false;
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.shippingBannerTimer = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.updateShippingBanner();
    this.setupShippingTooltip();
  }

  setupShippingTooltip() {
    const toggleTooltip = (icon, wrapperSelector) => {
      const wrapper = icon?.closest(wrapperSelector);
      const isOpen = wrapper?.classList.toggle('is-open');
      icon?.setAttribute('aria-expanded', isOpen);
      if (isOpen) {
        this.closeShippingTooltipOnClickOutside = (evt) => {
          if (!evt.target.closest(wrapperSelector)) {
            wrapper?.classList.remove('is-open');
            icon?.setAttribute('aria-expanded', 'false');
            document.removeEventListener('click', this.closeShippingTooltipOnClickOutside);
          }
        };
        document.addEventListener('click', this.closeShippingTooltipOnClickOutside);
      } else if (this.closeShippingTooltipOnClickOutside) {
        document.removeEventListener('click', this.closeShippingTooltipOnClickOutside);
      }
    };

    this.addEventListener('click', (e) => {
      const shippingIcon = e.target.closest('.cart-drawer__shipping-info-icon');
      const pillIcon = e.target.closest('.cart-item__description-pill-icon');
      if (shippingIcon) {
        e.preventDefault();
        toggleTooltip(shippingIcon, '.cart-drawer__shipping-info-trigger');
      } else if (pillIcon) {
        e.preventDefault();
        toggleTooltip(pillIcon, '.cart-item__pill-tooltip-trigger');
      }
    });

    this.addEventListener('keydown', (e) => {
      const shippingIcon = e.target.closest('.cart-drawer__shipping-info-icon');
      const pillIcon = e.target.closest('.cart-item__description-pill-icon');
      if ((shippingIcon || pillIcon) && (e.code === 'Enter' || e.code === 'Space')) {
        e.preventDefault();
        if (shippingIcon) toggleTooltip(shippingIcon, '.cart-drawer__shipping-info-trigger');
        else if (pillIcon) toggleTooltip(pillIcon, '.cart-item__pill-tooltip-trigger');
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.addEventListener('click', (event) => {
      if (!document.body.classList.contains('cart-page-variant')) {
        return;
      }
      event.preventDefault();
      if (!cartLink.dataset.cartDrawerA11y) {
        cartLink.dataset.cartDrawerA11y = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
      }
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      if (!document.body.classList.contains('cart-page-variant')) {
        return;
      }
      event.preventDefault();
      if (!cartLink.dataset.cartDrawerA11y) {
        cartLink.dataset.cartDrawerA11y = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
      }
      this.open(cartLink);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.updateShippingBanner();
    this.startShippingBannerTimer();
  }

  close() {
    this.classList.remove('active');
    this.querySelectorAll('.cart-drawer__shipping-info-trigger.is-open, .cart-item__pill-tooltip-trigger.is-open').forEach((el) => el.classList.remove('is-open'));
    if (this.closeShippingTooltipOnClickOutside) {
      document.removeEventListener('click', this.closeShippingTooltipOnClickOutside);
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.stopShippingBannerTimer();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;
      const inner =
        section.id === 'cart-icon-bubble'
          ? this.extractCartIconBubbleInner(sectionHtml) || this.getSectionInnerHTML(sectionHtml, section.selector)
          : this.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner) sectionElement.innerHTML = inner;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (!isShopifyCartPagePath() && document.body.classList.contains('cart-page-variant')) {
        this.open();
      }
      setTimeout(function () {
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'cart-drawer-render' } }));
          document.dispatchEvent(new CustomEvent('shopify:cart:update', { bubbles: true, detail: { source: 'cart-drawer-render' } }));
        } catch (e) {}
      }, 0);
    });
  }

  getEasternTimeParts() {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(new Date());
    const map = {};
    parts.forEach((part) => {
      map[part.type] = part.value;
    });

    return {
      weekday: map.weekday,
      hour: Number(map.hour),
      minute: Number(map.minute),
    };
  }

  /** Same-day copy + countdown: ET clock only (not cart contents). Visibility of the banner is Liquid (`cart_has_shippable_items`). */
  getShippingBannerMessage() {
    const { weekday, hour, minute } = this.getEasternTimeParts();
    const isBusinessDay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday);
    const totalMinutes = hour * 60 + minute;
    const cutOffMinutes = 11 * 60; // 11:00 ET

    // When forcing for testing: if past cutoff, simulate 8:30am ET to show countdown
    const useSimulatedTime = FORCE_SAME_DAY_SHIPPING_FOR_TESTING && (!isBusinessDay || totalMinutes >= cutOffMinutes);
    const effectiveMinutes = useSimulatedTime ? 8 * 60 + 30 : totalMinutes;

    if ((FORCE_SAME_DAY_SHIPPING_FOR_TESTING || (isBusinessDay && totalMinutes < cutOffMinutes)) && effectiveMinutes < cutOffMinutes) {
      const remainingMinutes = cutOffMinutes - effectiveMinutes;
      const hours = Math.floor(remainingMinutes / 60);
      const minutes = remainingMinutes % 60;
      return `Buy in the next ${hours}h ${minutes}m to ship today`;
    }

    return 'Buy now to ship next business day';
  }

  isSameDayShipping() {
    if (FORCE_SAME_DAY_SHIPPING_FOR_TESTING) return true;
    const { weekday, hour, minute } = this.getEasternTimeParts();
    const isBusinessDay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday);
    const totalMinutes = hour * 60 + minute;
    const cutOffMinutes = 11 * 60;
    return isBusinessDay && totalMinutes < cutOffMinutes;
  }

  /** Call after cart drawer HTML is replaced so the message matches the time rule (avoids stale placeholder until timer/open). */
  updateShippingBanner() {
    const bannerText = this.querySelector('.cart-drawer__shipping-text');
    const banner = this.querySelector('.cart-drawer__shipping-banner');
    if (!bannerText || !banner) return;
    bannerText.textContent = this.getShippingBannerMessage();
    banner.classList.toggle('is-same-day', this.isSameDayShipping());
  }

  startShippingBannerTimer() {
    this.stopShippingBannerTimer();
    this.shippingBannerTimer = window.setInterval(() => {
      this.updateShippingBanner();
    }, 30000);
  }

  stopShippingBannerTimer() {
    if (!this.shippingBannerTimer) return;
    window.clearInterval(this.shippingBannerTimer);
    this.shippingBannerTimer = null;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sel = selector || '.shopify-section';
    let el = doc.querySelector(sel);
    if (!el && sel === '.shopify-section') {
      el = doc.querySelector('[id^="shopify-section-"]');
    }
    if (el) return el.innerHTML;
    if (sel === '.shopify-section' && doc.body?.innerHTML) {
      return doc.body.innerHTML;
    }
    return '';
  }

  extractCartIconBubbleInner(html) {
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrap =
      doc.querySelector('.shopify-section') ||
      doc.querySelector('[id^="shopify-section-"]') ||
      doc.querySelector('#shopify-section-cart-icon-bubble');
    let inner = wrap ? wrap.innerHTML.trim() : '';
    if (!inner && doc.body?.innerHTML) {
      inner = doc.body.innerHTML.trim();
    }
    if (!inner) return '';
    const probe = new DOMParser().parseFromString(`<div>${inner}</div>`, 'text/html');
    const nestedA = probe.querySelector('a#cart-icon-bubble');
    return nestedA ? nestedA.innerHTML : inner;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
